import { isProblem, problemDetail, type Problem } from '@/shared/api'

const OTHER_DRAFT = 'Черновик уже другой — ИИ подготовил новый. Прочитайте его, прежде чем отправлять.'

/** Исход известен: сервер ответил отказом (4xx). Сеть и 5xx — запрос мог дойти, а черновик — уйти. */
export function refusedByServer(error: unknown): error is Problem {
  return isProblem(error) && error.status < 500
}

/**
 * Что сказать на месте карточки об отказе «Отправить».
 *
 * ⚠️ ГОВОРИТ НА КАЖДЫЙ ОТКАЗ И СРАЗУ: общий тост решил молчать в момент отказа (`meta.handledInPlace`),
 * и промолчи карточка — отказ не скажет никто. `after` — черновик, который показала шапка,
 * перечитанная после отказа; `undefined` — ещё перечитываем, и тогда слова не опираются на то, что
 * вместо черновика.
 *
 * Исход, названный сервером, — его словами; у `409` — только заголовком и только когда черновика не
 * осталось: что стало с черновиком, скажет перечитанная шапка. Повтор нигде не назван безопасным —
 * «проверьте ленту»: отказ приходит и на уже доставленное (письмо №20 в prima-inbox).
 */
export function draftRefusal(
  error: unknown,
  failedDraftId: string,
  after: { id: string } | null | undefined,
): string {
  const replaced = after != null && after.id !== failedDraftId

  if (!refusedByServer(error)) {
    if (replaced) return `Не знаем, дошёл ли прежний черновик до гостя — проверьте ленту. ${OTHER_DRAFT}`
    return after === null
      ? 'Не знаем, дошёл ли черновик до гостя — проверьте ленту.'
      : 'Не знаем, дошёл ли черновик до гостя — проверьте ленту, прежде чем отправлять снова.'
  }

  if (error.status === 409) {
    if (after === undefined) return 'Черновик уже не ждёт решения — проверяем, что с ним.'
    if (replaced) return OTHER_DRAFT
    if (after === null) return sentence(error.title, undefined)
    return 'Сервер не дал отправить этот черновик: он уже не ждёт решения. Если черновик останется на месте, проверьте ленту и только потом отправляйте снова.'
  }

  const words = sentence(error.title, problemDetail(error))
  if (replaced) return `${words} ${OTHER_DRAFT}`
  return after === null ? words : `${words} Проверьте ленту, прежде чем отправлять снова.`
}

/** Заголовок и пояснение сервера одной фразой — с точкой, чтобы за ней можно было продолжить. */
function sentence(title: string, detail: string | undefined): string {
  const end = (text: string) => (/[.!?…]$/.test(text) ? text : `${text}.`)
  return detail ? `${end(title)} ${end(detail)}` : end(title)
}
